import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { ApiRoute, BffInput } from "@slfinrtl/ui-bff-routes"
import { useCallback } from "react"
import { callBff } from "../../common/callBff"
import type { ErrorNotificationType } from "../useErrorNotificationCallback/useErrorNotificationCallback"
import { useErrorNotificationCallback } from "../useErrorNotificationCallback/useErrorNotificationCallback"
import { useExtractErrorMessage } from "../useExtractErrorMessage/useExtractErrorMessage"

/**
 * @param route The Bff route to call. Usually imported via @slfinrtl/ui-bff-routes
 * @param errorNotificationType Controls how to display errormessages. Every
 * error will still be thrown to enable additional errorhandling from outside. Can be one of the following values:
 * "inline": An inline notification will be displayed. In this case, the
 *      corresponding Container must be wrapped with the withErrorHandler HOC and use
 *      <ErrorNotificationInline /> component to display the error.
 * "global": A global error banner will be displayed on top of the page
 * "none": Errors will not be handled by this hook.
 *
 * @returns (input) => Promise<ResponseType>
 * @param input Contains all pathparams, queryparams and request body
 *
 */
export const useBffDynamic = <RequestPathParams, RequestQueryParams, RequestBody, ResponseType>(
	route: ApiRoute<RequestPathParams, RequestQueryParams, RequestBody, ResponseType>,
	errorNotificationType: ErrorNotificationType,
): ((input: BffInput<RequestPathParams, RequestQueryParams, RequestBody>) => Promise<ResponseType>) => {
	const { dealershipId, environment, getAuthorizationHeader, locale, marketplaceKey } = useDealerContext()
	const extractErrorMessage = useExtractErrorMessage()

	const errorNotificationCallback = useErrorNotificationCallback(errorNotificationType)

	return useCallback(
		(input: { body: RequestBody; pathParams: RequestPathParams; queryParams: RequestQueryParams }) => {
			return callBff(
				{
					dealershipId,
					environment,
					getAuthorizationHeader,
					locale,
					marketplaceKey,
				},
				route,
				input,
			)
				.then((value) => {
					errorNotificationCallback?.(undefined)
					return value
				})
				.catch((error) => {
					errorNotificationCallback?.(extractErrorMessage(error))
					throw error
				})
		},
		[
			route,
			getAuthorizationHeader,
			locale,
			environment,
			dealershipId,
			marketplaceKey,
			extractErrorMessage,
			errorNotificationCallback,
		],
	)
}
